import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';

export interface AutoLoopSettings {
  enabled: boolean;
  duration: number; // in seconds
}

@Injectable({
  providedIn: 'root'
})
export class AutoLoopService {
  private readonly AUTO_LOOP_SETTINGS_KEY = 'drawfiend-auto-loop-settings';
  private settingsSubject = new BehaviorSubject<AutoLoopSettings>(this.getDefaultSettings());

  constructor() {
    // Load settings from localStorage on startup
    this.loadSettings();
  }

  /**
   * Get default settings
   */
  private getDefaultSettings(): AutoLoopSettings {
    return {
      enabled: true,
      duration: 20
    };
  }

  /**
   * Load settings from localStorage
   */
  private loadSettings(): void {
    try {
      const saved = localStorage.getItem(this.AUTO_LOOP_SETTINGS_KEY);
      if (saved) {
        const settings = JSON.parse(saved);
        const validatedSettings: AutoLoopSettings = {
          enabled: settings.enabled !== undefined ? settings.enabled : true,
          duration: Math.max(5, settings.duration || 20) // Enforce minimum 5 seconds
        };
        this.settingsSubject.next(validatedSettings);
      }
    } catch (error) {
      console.warn('Failed to load auto-loop settings:', error);
    }
  }

  /**
   * Save settings to localStorage
   */
  saveSettings(settings: AutoLoopSettings): void {
    try {
      const validatedSettings: AutoLoopSettings = {
        enabled: settings.enabled,
        duration: Math.max(5, settings.duration) // Enforce minimum 5 seconds
      };
      localStorage.setItem(this.AUTO_LOOP_SETTINGS_KEY, JSON.stringify(validatedSettings));
      this.settingsSubject.next(validatedSettings);
      console.log('Auto-loop settings saved:', validatedSettings);
    } catch (error) {
      console.warn('Failed to save auto-loop settings:', error);
    }
  }

  /**
   * Get current settings
   */
  getSettings(): AutoLoopSettings {
    return this.settingsSubject.value;
  }

  /**
   * Get settings as observable
   */
  getSettings$(): Observable<AutoLoopSettings> {
    return this.settingsSubject.asObservable();
  }
}